import type { ApiErrorCode, Poll, PollResults } from "@vote/shared";
import { getSharedIdentification } from "./identity";
import type { ApiFailure, IdentityStore, PageMetaReader, VoteApi, Wait } from "./ports";
import { withServiceBusyRetry } from "./service-busy-retry";

/** 投票ウィジェットの表示に必要な状態だけをUIへ公開する。 */
export type PollFlowState =
  | { kind: "loading" }
  | { kind: "scheduled"; poll: Poll }
  | { kind: "select"; poll: Poll; results: PollResults; selectedChoiceId: string | null }
  | { kind: "submitting"; poll: Poll; results: PollResults; selectedChoiceId: string }
  | { kind: "rejected" }
  | { kind: "results"; poll: Poll; results: PollResults; myChoiceId: string }
  | {
      kind: "closed";
      poll: Poll;
      // 終了した投票はサーバが票数に関係なく割合以上を返す（D-059 補記）ため、初期集計中を型から除く。
      results: Exclude<PollResults, { kind: "collecting" }>;
      myChoiceId: null;
    }
  | { kind: "error"; code: ApiErrorCode | "unknown"; message: string };

/** 状態変化のたびにUIへ最新状態を渡す購読者。 */
type PollFlowSubscriber = (state: PollFlowState) => void;

/** 投票フローが外部へ依頼する機能と埋め込み先のサイトキー。 */
interface PollFlowDependencies {
  siteKey: string;
  api: VoteApi;
  identityStore: IdentityStore;
  pageMetaReader: PageMetaReader;
  wait: Wait;
}

/** API失敗を、UIが回復可能性を判定できる構造化状態へ変換する。 */
function errorState(failure: ApiFailure): Extract<PollFlowState, { kind: "error" }> {
  return { kind: "error", code: failure.error.code, message: failure.error.message };
}

/** 予期しない例外を unknown として保持し、既知のAPIエラーと混同しない。 */
function unknownErrorState(error: unknown): Extract<PollFlowState, { kind: "error" }> {
  return {
    kind: "error",
    code: "unknown",
    message: error instanceof Error ? error.message : "unknown_error",
  };
}

/**
 * identify → resolve → vote の順序、状態遷移、限定再送を一か所で管理する。
 * DOM・fetch・storage・timer はすべて注入されたポート越しに利用する。
 */
export class PollFlow {
  private state: PollFlowState = { kind: "loading" };
  private readonly subscribers = new Set<PollFlowSubscriber>();

  public constructor(private readonly dependencies: PollFlowDependencies) {}

  /** 現在の表示状態を、主にUIの初期描画とテストへ返す。 */
  public getState(): PollFlowState {
    return this.state;
  }

  /** 購読開始時にも現在値を通知し、解除関数を返す。 */
  public subscribe(subscriber: PollFlowSubscriber): () => void {
    this.subscribers.add(subscriber);
    subscriber(this.state);
    return () => this.subscribers.delete(subscriber);
  }

  /** 記事の投票を読み込み、回答状況に対応する状態まで進める。 */
  public async start(): Promise<void> {
    this.publish({ kind: "loading" });

    try {
      const identityResult = await getSharedIdentification({
        siteKey: this.dependencies.siteKey,
        api: this.dependencies.api,
        identityStore: this.dependencies.identityStore,
        wait: this.dependencies.wait,
      });
      if (!identityResult.ok) {
        this.publish(errorState(identityResult));
        return;
      }

      // identify の採用IDを即座に保存し、後続のresolveを同じ訪問者として送る。
      this.dependencies.identityStore.set(identityResult.data.visitorId);
      const pageMeta = this.dependencies.pageMetaReader.read();
      const resolveResult = await withServiceBusyRetry(
        () =>
          this.dependencies.api.resolvePoll({
            siteKey: this.dependencies.siteKey,
            visitorId: identityResult.data.visitorId,
            ...pageMeta,
          }),
        this.dependencies.wait,
      );

      if (!resolveResult.ok) {
        this.publish(errorState(resolveResult));
        return;
      }

      // Cookie優先で変わり得る採用IDを保存し、ブラウザ永続値との不一致を自己修復する。
      this.dependencies.identityStore.set(resolveResult.data.visitorId);
      const { poll, results, myChoiceId } = resolveResult.data;

      // 公開後に開始を未来へ変更した場合も、回答済み結果より開始前表示を優先する。
      if (poll.availability === "scheduled") {
        this.publish({ kind: "scheduled", poll });
        return;
      }

      // 回答済みなら、受付終了後でも本人が結果を確認できる結果状態を優先する。
      if (myChoiceId !== null) {
        this.publish({ kind: "results", poll, results, myChoiceId });
        return;
      }

      if (poll.availability !== "open") {
        // 回答済みは上の results 分岐へ進むため、未回答の終了状態だけがここへ到達する。
        // 終了後の初期集計中は契約違反（サーバの toPublicResults は返さない）。SDKは成功応答を実行時検証
        // しないため、closed 状態の型へ絞り込むこの検査だけを置き、結果の無いカードを描かずエラーカードにする
        // （割合が欠けた応答への getChoicePercentage と同じ保険）。
        if (results.kind === "collecting") {
          throw new Error("受付終了の投票に初期集計中の結果が返りました");
        }
        this.publish({ kind: "closed", poll, results, myChoiceId: null });
        return;
      }

      // 選択中にも公開範囲内の集計（full なら回答者数）を表示できるよう、resolve済みの集計を状態に保持する。
      this.publish({ kind: "select", poll, results, selectedChoiceId: null });
    } catch (error) {
      this.publish(unknownErrorState(error));
    }
  }

  /** 選択画面に存在する選択肢だけを採用し、UIと状態を同期する。 */
  public selectChoice(choiceId: string): void {
    if (this.state.kind !== "select") {
      return;
    }
    if (!this.state.poll.choices.some((choice) => choice.id === choiceId)) {
      return;
    }
    this.publish({ ...this.state, selectedChoiceId: choiceId });
  }

  /** 選択済み回答を送信し、成功レスポンスの集計値で結果表示へ進める。 */
  public async submitVote(): Promise<void> {
    if (this.state.kind !== "select" || this.state.selectedChoiceId === null) {
      return;
    }

    const { poll, results, selectedChoiceId } = this.state;
    // 送信中も選択画面を維持するため、直前に表示していた集計をそのまま引き継ぐ。
    this.publish({ kind: "submitting", poll, results, selectedChoiceId });

    try {
      const firstResult = await this.sendVoteWithBusyRetry(poll.id, selectedChoiceId);
      if (firstResult.ok) {
        this.publish({
          kind: "results",
          poll,
          results: firstResult.data.results,
          myChoiceId: selectedChoiceId,
        });
        return;
      }

      // visitor_requiredだけは新しいIDを取得し、同じ投票を一度だけ再送して回復する。
      if (firstResult.error.code === "visitor_required") {
        const identityResult = await getSharedIdentification({
          siteKey: this.dependencies.siteKey,
          api: this.dependencies.api,
          identityStore: this.dependencies.identityStore,
          wait: this.dependencies.wait,
        });
        if (!identityResult.ok) {
          this.publish(errorState(identityResult));
          return;
        }
        this.dependencies.identityStore.set(identityResult.data.visitorId);
        const retryResult = await this.sendVoteWithBusyRetry(poll.id, selectedChoiceId);
        if (retryResult.ok) {
          this.publish({
            kind: "results",
            poll,
            results: retryResult.data.results,
            myChoiceId: selectedChoiceId,
          });
          return;
        }
        if (retryResult.error.code === "poll_not_open") {
          this.publish({ kind: "rejected" });
        } else {
          this.publish(errorState(retryResult));
        }
        return;
      }

      // 開いたまま期間を跨いだ場合はサーバの最新状態へ戻し、回答は自動で再送しない。
      if (firstResult.error.code === "poll_not_open") {
        this.publish({ kind: "rejected" });
        return;
      }
      this.publish(errorState(firstResult));
    } catch (error) {
      this.publish(unknownErrorState(error));
    }
  }

  /** 拒否通知を閉じて最新状態を再取得する。連打やタイマー競合では一度だけ進める。 */
  public async showLatestAfterRejection(): Promise<void> {
    if (this.state.kind === "rejected") {
      await this.start();
    }
  }

  /** 結果画面から、現在の回答を選択済みにした変更画面へ戻す。 */
  public changeAnswer(): void {
    // 受付終了後の変更APIは必ず409になるため、UIの表示有無に依存せずcoreでも禁止する。
    if (this.state.kind !== "results" || this.state.poll.availability !== "open") {
      return;
    }
    this.publish({
      kind: "select",
      poll: this.state.poll,
      results: this.state.results,
      selectedChoiceId: this.state.myChoiceId,
    });
  }

  /** エラー状態から初期読込をやり直す。 */
  public async retry(): Promise<void> {
    if (this.state.kind === "error") {
      await this.start();
    }
  }

  /** 現在の採用IDを添えて投票し、一時的混雑だけを一度再送する。 */
  private async sendVoteWithBusyRetry(pollId: string, choiceId: string) {
    const visitorId = this.dependencies.identityStore.get();
    return withServiceBusyRetry(
      () =>
        this.dependencies.api.vote({
          siteKey: this.dependencies.siteKey,
          pollId,
          choiceId,
          ...(visitorId === undefined ? {} : { visitorId }),
        }),
      this.dependencies.wait,
    );
  }

  /** 状態を更新した後、全購読者へ同じスナップショットを通知する。 */
  private publish(state: PollFlowState): void {
    this.state = state;
    for (const subscriber of this.subscribers) {
      subscriber(state);
    }
  }
}
